import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Newspaper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIP_CACHE_KEY = 'dividrip:moversStrip:cache';
let inflight = null;

// Tickers that currently have Market Movers news. Reads the strip's
// sessionStorage cache first; otherwise one shared fetch per page load.
function loadNewsTickers() {
  try {
    const raw = sessionStorage.getItem(STRIP_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const bt = parsed?.data?.by_ticker;
      if (bt && Object.keys(bt).length) return Promise.resolve(new Set(Object.keys(bt)));
    }
  } catch { /* noop */ }
  if (!inflight) {
    inflight = axios.get(`${API}/news/movers`, { params: { use_top_movers: 1, top_movers_limit: 20, limit_per_ticker: 1 } })
      .then((r) => new Set(Object.keys(r.data?.by_ticker || {})))
      .catch(() => new Set());
  }
  return inflight;
}

export function useNewsTickers() {
  const [set, setSet] = useState(null);
  useEffect(() => { let on = true; loadNewsTickers().then((s) => { if (on) setSet(s); }); return () => { on = false; }; }, []);
  return set;
}

// Sep 2026 — per-list lookup so Top Movers rows outside the strip's default
// query (All / Non-Dividend filters) still get a badge. One shared request per
// distinct ticker set; server caches by key for 5 min.
const extraCache = new Map();
export function useNewsTickersFor(tickers) {
  const base = useNewsTickers();
  const [extra, setExtra] = useState(null);
  const key = useMemo(() => [...new Set((tickers || []).filter(Boolean))].sort().join(','), [tickers]);
  useEffect(() => {
    if (!key) return undefined;
    let on = true;
    let p = extraCache.get(key);
    if (!p) {
      p = axios.get(`${API}/news/movers`, { params: { tickers: key, limit_per_ticker: 1 } })
        .then((r) => new Set(Object.keys(r.data?.by_ticker || {})))
        .catch(() => new Set());
      extraCache.set(key, p);
    }
    p.then((s) => { if (on) setExtra(s); });
    return () => { on = false; };
  }, [key]);
  return useMemo(() => {
    if (!base && !extra) return null;
    return new Set([...(base || []), ...(extra || [])]);
  }, [base, extra]);
}

// Small "News" pill after a ticker; deep-links to /news#ticker-XYZ.
export default function NewsBadge({ ticker, newsTickers }) {
  if (!newsTickers || !newsTickers.has(ticker)) return null;
  return (
    <Link
      to={`/news#ticker-${ticker}`}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border border-sky-400/60 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors"
      title={`Read today's ${ticker} news`}
      data-testid={`news-badge-${ticker}`}
    >
      <Newspaper className="w-3 h-3" /> News
    </Link>
  );
}
